import React, { useState } from "react"
import { useScroll } from "../../hooks/useScroll"
import { classNames } from "../../utils/classNames"
import { trackButtonClick } from "../../analytics"

const Navbar = ({
  logoUrl,
  language,
  languageOptions,
  onLanguageChange,
  t,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const isScrolled = useScroll()

  const navLinks = [
    { href: "#hero", label: t.home },
    { href: "#about", label: t.about },
    { href: "#services", label: t.services },
    { href: "#contact", label: t.contact },
  ]

  const scrollToSection = (e, href) => {
    e.preventDefault()
    const section = document.querySelector(href)
    if (section) {
      section.scrollIntoView({ behavior: "smooth" })
      trackButtonClick(`nav_${href.replace('#', '')}`, 'navigation')
    }
    setIsOpen(false)
  }

  const handleLanguageChange = (e) => {
    const selectedLanguage = e.target.value
    onLanguageChange(selectedLanguage)
    trackButtonClick(`language_change_${selectedLanguage}`, "navigation")
    setIsOpen(false)
  }

  return (
    <nav
      className={classNames(
        "fixed w-full z-50 transition-all duration-300 ease-in-out",
        isScrolled || isOpen ? "bg-white shadow-lg" : "bg-transparent"
      )}
    >
      <div className="container mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <a
            href="#hero"
            className="flex items-center"
            onClick={(e) => scrollToSection(e, "#hero")}
          >
            <img
              src={logoUrl || "/web_logo.jpg"}
              alt="Codegrove 로고"
              className="h-10 w-auto"
              onError={(e) => {
                e.target.onerror = null
                e.target.src =
                  "https://placehold.co/150x50/FFFFFF/333333?text=Logo+Error&font=Inter"
              }}
            />
          </a>
          <div className="hidden md:flex items-center gap-4">
            <div className="flex space-x-6">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className={classNames(
                    "px-3 py-2 rounded-md text-sm font-medium transition-colors",
                    isScrolled || isOpen
                      ? "text-gray-700 hover:text-green-600"
                      : "text-gray-200 hover:text-white"
                  )}
                >
                  {link.label}
                </a>
              ))}
            </div>
            <select
              value={language}
              onChange={handleLanguageChange}
              aria-label={t.languageAriaLabel}
              className={classNames(
                "rounded-md text-sm font-medium px-2 py-1 border focus:outline-none focus:ring-2 focus:ring-green-500",
                isScrolled || isOpen
                  ? "bg-white text-gray-700 border-gray-300"
                  : "bg-white/10 text-white border-white/50"
              )}
            >
              {languageOptions.map((option) => (
                <option key={option.code} value={option.code} className="text-gray-800">
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={classNames(
                "focus:outline-none",
                isScrolled || isOpen
                  ? "text-gray-700 hover:text-green-600"
                  : "text-gray-200 hover:text-white"
              )}
              aria-label={t.menuToggle}
            >
              <svg className="h-6 w-6 fill-current" viewBox="0 0 24 24">
                {isOpen ? (
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M18.278 16.864a1 1 0 0 1-1.414 1.414l-4.829-4.828-4.828 4.828a1 1 0 0 1-1.414-1.414l4.828-4.828-4.828-4.828a1 1 0 0 1 1.414-1.414l4.829 4.828 4.828-4.828a1 1 0 1 1 1.414 1.414l-4.828 4.828 4.828 4.828z"
                  />
                ) : (
                  <path
                    fillRule="evenodd"
                    d="M4 5h16a1 1 0 0 1 0 2H4a1 1 0 1 1 0-2zm0 6h16a1 1 0 0 1 0 2H4a1 1 0 0 1 0-2zm0 6h16a1 1 0 0 1 0 2H4a1 1 0 0 1 0-2z"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>
      {isOpen && (
        <div className="md:hidden bg-white shadow-lg">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="text-gray-700 hover:text-green-600 block px-3 py-2 rounded-md text-base font-medium transition-colors"
              >
                {link.label}
              </a>
            ))}
            <div className="pt-2 border-t border-gray-200">
              <label
                htmlFor="mobile-language-select"
                className="block px-3 py-2 text-sm font-medium text-gray-600"
              >
                {t.languageAriaLabel}
              </label>
              <div className="px-3 pb-2">
                <select
                  id="mobile-language-select"
                  value={language}
                  onChange={handleLanguageChange}
                  className="w-full rounded-md text-sm font-medium px-3 py-2 border border-gray-300 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  {languageOptions.map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  )
}

export default Navbar
